import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from 'environments/environment';
import { catchError } from 'rxjs/operators';
import { SxErrorService } from '../../../services/sx-error.service';
import { AnalyzeReadingResult, AnalyzeReportParameters, TemperatureMeteringPoint } from '@model';
import { AnalyzeConsumptionType, DatePeriodType, MeteringType } from '@enums';
import dayjs from 'dayjs';

@Injectable({
  providedIn: 'root'
})
export class AnalyzeService {
  constructor(private http: HttpClient, private sxErrorService: SxErrorService) {}

  getConsumptions(
    masterMeteringPointId: number,
    meteringPointIds: number[],
    from: Date,
    to: Date,
    compareYears: number[],
    resolution: DatePeriodType,
    type: string,
    unit: string,
    meteringType: MeteringType,
    excludeWeekends: boolean,
    effctSignatureLine: boolean
  ): Observable<AnalyzeReadingResult[]> {
    let fixedDateTo = dayjs(to)
      .add(1, 'd')
      .startOf('d')
      .toDate();

    return this.http
      .post<AnalyzeReadingResult[]>(`${environment.apiEndpoint}/meteringpoints/consumptions`, {
        masterMeteringPointId: masterMeteringPointId,
        meteringPointIds: meteringPointIds,
        from: dayjs(from).format('YYYY-MM-DD'),
        to: dayjs(fixedDateTo).format('YYYY-MM-DD'),
        compareYears: compareYears ? compareYears : [],
        resolution: resolution,
        type: type,
        unit: unit,
        meteringType: meteringType,
        excludeWeekends: !!excludeWeekends,
        effctSignatureLine: !!effctSignatureLine
      })
      .pipe(catchError((error: any) => this.sxErrorService.handleError(error)));
  }

  getMeterReadings(
    masterMeteringPointId: number,
    meteringPointIds: number[],
    from: Date,
    to: Date,
    compareYears: number[],
    resolution: DatePeriodType,
    type: string,
    meteringType: MeteringType,
    excludeWeekends: boolean
  ): Observable<AnalyzeReadingResult[]> {
    let fixedDateTo = dayjs(to)
      .add(1, 'd')
      .startOf('d')
      .toDate();

    return this.http
      .post<AnalyzeReadingResult[]>(`${environment.apiEndpoint}/meteringpoints/readings`, {
        masterMeteringPointId: masterMeteringPointId,
        meteringPointIds: meteringPointIds,
        from: dayjs(from).format('YYYY-MM-DD'),
        to: dayjs(fixedDateTo).format('YYYY-MM-DD'),
        compareYears: compareYears ? compareYears : [],
        resolution: resolution,
        type: type,
        meteringType: meteringType,
        excludeWeekends: !!excludeWeekends
      })
      .pipe(catchError((error: any) => this.sxErrorService.handleError(error)));
  }

  exportToExcel(key: string, exportSettings: AnalyzeReportParameters): Observable<any> {
    return this.http
      .post<any>(`${environment.apiEndpoint}/reports/${key}/analyze/excelreport`, exportSettings, {
        responseType: 'blob' as 'json'
      })
      .pipe(catchError((error: any) => this.sxErrorService.handleError(error)));
  }

  getTemperatureData(
    masterMeteringPointId: number,
    meteringPointIds: number[],
    from: Date,
    to: Date
  ): Observable<TemperatureMeteringPoint[]> {
    return this.http
      .post<TemperatureMeteringPoint[]>(`${environment.apiEndpoint}/meteringpoints/temperatures`, {
        masterMeteringPointId: masterMeteringPointId,
        meteringPointIds: meteringPointIds,
        from: dayjs(from).format('YYYY-MM-DD'),
        to: dayjs(to).format('YYYY-MM-DD')

      })
      .pipe(catchError((error: any) => this.sxErrorService.handleError(error)));
  }

  createReportKey(): Observable<string> {
    return this.http
      .get<string>(`${environment.apiEndpoint}/MeteringPoints/key`)
      .pipe(catchError((error: any) => this.sxErrorService.handleError(error)));
  }

  getEconomyConsumption(
    meteringPointIds: number[],
    from: Date,
    to: Date,
    resolution: DatePeriodType,
    unit: string,
    compareYears: number[],
    meteringType:MeteringType
  ): Observable<AnalyzeReadingResult[]> {
    return this.http
      .post<AnalyzeReadingResult[]>(`${environment.apiEndpoint}/meteringpoints/economy`, {
        meteringPointIds: meteringPointIds,
        from: dayjs(from).format('YYYY-MM-DD'),
        to: dayjs(to).format('YYYY-MM-DD'),
        resolution: resolution,
        unit: unit,
        compareYears: compareYears,
        meteringType: meteringType
      })
      .pipe(catchError((error: any) => this.sxErrorService.handleError(error)));
  }

  getEconomyPrognosis(
    meteringPointIds: number[],
    from: Date,
    to: Date,
    resolution: DatePeriodType,
    unit: string,
    meteringType: MeteringType
  ): Observable<AnalyzeReadingResult[]> {
    return this.http
      .post<AnalyzeReadingResult[]>(`${environment.apiEndpoint}/meteringpoints/economy`, {
        meteringPointIds: meteringPointIds,
        from: dayjs(from).format('YYYY-MM-DD'),
        to: dayjs(to).format('YYYY-MM-DD'),
        resolution: resolution,
        unit: unit,
        type: AnalyzeConsumptionType.EconomyHistoricalForecast,
        meteringType: meteringType
      })
      .pipe(catchError((error: any) => this.sxErrorService.handleError(error)));
  }
}
